'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import { cn } from '@/shared/utils/styling';

type SelectContentProps = React.ComponentProps<
  typeof SelectPrimitive.Content
> & {
  hideScrollButtons?: boolean;
  classNameViewport?: string;
};

type SelectTriggerProps = React.ComponentProps<
  typeof SelectPrimitive.Trigger
> & {
  size?: 'sm' | 'md' | 'default';
  withChevron?: boolean;
  floatingLabel?: string;
  placeholder?: string;
};

function Select({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function renderChevronIcon(withChevron: boolean) {
  if (!withChevron) {
    return null;
  }

  return (
    <SelectPrimitive.Icon asChild>
      <ChevronDownIcon className="size-[2.4rem]" />
    </SelectPrimitive.Icon>
  );
}

function SelectTrigger({
  className,
  floatingLabel,
  size = 'default',
  withChevron = false,
  placeholder,
  children,
  ...props
}: SelectTriggerProps) {
  const hasValue = props.value !== undefined && props.value !== '';

  let triggerHeight = '';

  if (size === 'md') {
    triggerHeight = 'h-[6rem]';
  } else if (size === 'default') {
    triggerHeight = 'h-[4rem]';
  }

  const isCustomChev = placeholder !== undefined || floatingLabel !== undefined;

  const ChevIcon = isCustomChev ? (
    renderChevronIcon(withChevron)
  ) : withChevron ? (
    <SelectPrimitive.Icon asChild>
      <ChevronDownIcon className="size-4 opacity-50" />
    </SelectPrimitive.Icon>
  ) : null;

  return (
    <SelectPrimitive.Trigger
      data-size={size}
      className={cn(
        'border-input',
        'data-[placeholder]:text-muted-foreground',
        'focus-visible:border-ring focus-visible:ring-ring/50',

        'aria-invalid:border-destructive aria-invalid:bg-light-error',

        'disabled:cursor-not-allowed disabled:opacity-50',

        'dark:bg-input/30 dark:hover:bg-input/50',

        'flex w-full items-center justify-between gap-2',
        'rounded-[1.6rem] bg-muted px-6 h-[6rem]',
        'whitespace-nowrap min-w-0',

        className,
        triggerHeight
      )}
      {...props}>
      <div className="flex flex-col min-w-0 flex-1 items-start">
        {floatingLabel && (
          <label
            className={cn(
              'text-muted-foreground transition-all',
              hasValue ? 'p6' : 'p3'
            )}>
            {floatingLabel}
          </label>
        )}
        <div className="min-w-0 w-full flex items-start">
          {children ? (
            <div className="min-w-0 w-full truncate text-left">{children}</div>
          ) : (
            <span className="text-muted-foreground p3">{placeholder}</span>
          )}
        </div>
      </div>
      {ChevIcon}
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  hideScrollButtons = true,
  classNameViewport,
  ...props
}: SelectContentProps) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-80 max-h-(--radix-select-content-available-height) min-w-[8rem] max-w-[var(--radix-select-trigger-width)] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        position={position}
        {...props}>
        {!hideScrollButtons && <SelectScrollUpButton className="h-full" />}
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] scroll-my-1',
            classNameViewport
          )}>
          {children}
        </SelectPrimitive.Viewport>
        {!hideScrollButtons && <SelectScrollDownButton />}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
}

type SelectItemProps = React.ComponentProps<typeof SelectPrimitive.Item> & {
  showOptionCheckmark?: boolean;
};

function SelectItem({
  className,
  children,
  showOptionCheckmark = true,
  ...props
}: SelectItemProps) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 *:[span]:last:flex-1 *:[span]:last:min-w-0",
        className
      )}
      {...props}>
      {showOptionCheckmark && (
        <span className="absolute right-4 flex size-3.5 items-center justify-center">
          <SelectPrimitive.ItemIndicator>
            <CheckIcon className="size-7 text-primary" />
          </SelectPrimitive.ItemIndicator>
        </span>
      )}
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className
      )}
      {...props}>
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className
      )}
      {...props}>
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
